import React, { Fragment, useState, useMemo } from "react";
import { Button, Text, Menu, MenuItem, Portal, Box } from "@chakra-ui/react";
import { CheckIcon } from "../../../icons";

const MixtapeFilterSort = (props) => {
  const [isGenresOpen, setIsGenresOpen] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(false);

  const sortOptions = [
    { text: "Marmoset Default", value: "default" },
    { text: "Newest to Oldest", value: "newest" },
    { text: "Oldest to Newest", value: "oldest" },
    { text: "A - Z", value: "a-z" },
    { text: "Z - A", value: "z-a" },
  ];

  const selectedGenres = props.selectedGenres || [];
  const filterOptions = props.filterOptions || [];

  const handleFilterOrSort = (_type, selected) => {
    if (!selected.altered) return;
    props.updateMixtapes(selected);
  };

  const genreItems = useMemo(() => {
    const noneSelected = selectedGenres.length === 0;

    return filterOptions.map((item) => {
      const value = item.value;
      const label = item.label ?? item.text ?? String(item.value ?? "");
      const isAll = value === "";
      const isSelected = selectedGenres.includes(value);
      const isActive = isAll ? noneSelected : isSelected;

      const handleClick = () => {
        if (isAll) {
          handleFilterOrSort("genres", {
            name: "genres",
            values: [],
            altered: true,
          });
        } else {
          handleFilterOrSort("genres", {
            name: "genres",
            values: toggleValue(selectedGenres, value),
            altered: true,
          });
        }
      };

      return (
        <Fragment key={`${value}-${label}`}>
          <MenuItem
            as="button"
            rounded="md"
            h="36px"
            onClick={handleClick}
            cursor="pointer"
            color={isActive ? "var(--orange)" : "var(--purple)"}
            _hover={{ color: "var(--orange)" }}
            data-cy={`genres-option-${String(value).replaceAll(" ", "-")}`}
          >
            <Text fontFamily="TTCOMMONSMEDIUM" fontSize="14px">
              {label}
            </Text>
            {isActive && (
              <Box marginLeft="auto">
                <CheckIcon
                  width="15px"
                  height="15px"
                  fillColor="var(--purple)"
                  minWidth="15px"
                />
              </Box>
            )}
          </MenuItem>
        </Fragment>
      );
    });
  }, [filterOptions, selectedGenres]);

  return (
    <div className="mixtape-filter-sort">
      {/* Genres */}
      <Menu.Root
        id="genres"
        autoSelect={false}
        closeOnSelect={false}
        onOpenChange={(details) => setIsGenresOpen(details.open)}
        isLazy
      >
        <Menu.Trigger asChild>
          <Button
            display="flex"
            alignItems="center"
            textTransform="uppercase"
            backgroundColor={!isGenresOpen ? "white" : "var(--purple)"}
            border={
              isGenresOpen
                ? "solid 2px var(--purple)"
                : "solid 2px var(--light-purple)"
            }
            height="40px"
            borderRadius="20px"
            px="20px"
            variant="plain"
            color={!isGenresOpen ? "var(--purple)" : "white"}
            _hover={{
              backgroundColor: "var(--purple)",
              color: "white",
              borderColor: "var(--purple)",
            }}
            _active={{
              backgroundColor: "var(--purple)",
              color: "white",
              borderColor: "var(--purple)",
            }}
            overflow="hidden"
            data-cy="genres-button"
          >
            <Text
              fontSize=".8rem"
              letterSpacing="0.128em"
              fontFamily="TTCommonsMedium"
            >
              Top Genres
            </Text>
          </Button>
        </Menu.Trigger>
        <Portal>
          <Menu.Positioner>
            <Menu.Content
              shadow="lg"
              py="4"
              px="3"
              overflowY="auto"
              minW={{ base: "13rem", smTo2xl: "15rem" }}
              maxH="25rem"
              borderRadius="20px"
              background="var(--light-purple)"
            >
              {/* Reset */}
              <MenuItem
                as="button"
                fontWeight="semibold"
                rounded="md"
                onClick={() =>
                  handleFilterOrSort("genres", {
                    name: "genres",
                    values: [],
                    altered: true,
                  })
                }
                position="relative"
                cursor="pointer"
                mb="15px"
                _hover={{ color: "var(--purple)" }}
              >
                <Text
                  position="absolute"
                  top="0px"
                  right="0px"
                  fontSize="0.6875rem"
                  fontFamily="TTCOMMONSMEDIUM"
                  color="gray.400"
                >
                  Reset
                </Text>
              </MenuItem>

              {/* Items */}
              {genreItems}
            </Menu.Content>
          </Menu.Positioner>
        </Portal>
      </Menu.Root>

      {/* Sort */}
      <Menu.Root
        id="sort"
        autoSelect={false}
        closeOnSelect={true}
        onOpenChange={(details) => setIsSortOpen(details.open)}
        isLazy
      >
        <Menu.Trigger asChild>
          <Button
            display="flex"
            alignItems="center"
            textTransform="uppercase"
            backgroundColor={!isSortOpen ? "white" : "var(--purple)"}
            border={
              isSortOpen
                ? "solid 2px var(--purple)"
                : "solid 2px var(--light-purple)"
            }
            height="40px"
            borderRadius="20px"
            px="20px"
            variant="plain"
            color={!isSortOpen ? "var(--purple)" : "white"}
            _hover={{
              backgroundColor: "var(--purple)",
              color: "white",
              borderColor: "var(--purple)",
            }}
            _active={{
              backgroundColor: "var(--purple)",
              color: "white",
              borderColor: "var(--purple)",
            }}
            overflow="hidden"
            data-cy="sort-button"
          >
            <Text
              fontSize=".8rem"
              letterSpacing="0.128em"
              fontFamily="TTCommonsMedium"
            >
              Sort
            </Text>
          </Button>
        </Menu.Trigger>
        <Portal>
          <Menu.Positioner>
            <Menu.Content
              shadow="lg"
              py="4"
              px="3"
              minW="13rem"
              borderRadius="20px"
              background="var(--light-purple)"
            >
              {/* Reset Sort */}
              <MenuItem
                as="button"
                fontWeight="semibold"
                rounded="md"
                onClick={() =>
                  handleFilterOrSort("sort", {
                    name: "sort",
                    value: "default",
                    altered: true,
                  })
                }
                position="relative"
                cursor="pointer"
                mb="15px"
                _hover={{ color: "var(--purple)" }}
              >
                <Text
                  position="absolute"
                  top="0px"
                  right="0px"
                  fontSize="0.6875rem"
                  fontFamily="TTCOMMONSMEDIUM"
                  color="gray.400"
                >
                  Reset
                </Text>
              </MenuItem>
              {sortOptions.map((item) => {
                const isSelected = props.selectedSort === item.value;
                return (
                  <MenuItem
                    as="button"
                    key={item.value}
                    h="36px"
                    cursor="pointer"
                    onClick={() =>
                      handleFilterOrSort("sort", {
                        name: "sort",
                        value: item.value,
                        altered: true,
                      })
                    }
                    fontFamily="TTCOMMONSMEDIUM"
                    fontSize="14px"
                    color={isSelected ? "var(--orange)" : "var(--purple)"}
                    _hover={{ color: "var(--orange)" }}
                  >
                    <Text fontSize="14px">{item.text}</Text>
                    {isSelected && (
                      <Box marginLeft="auto">
                        <CheckIcon
                          width="15px"
                          height="15px"
                          fillColor="var(--purple)"
                          minWidth="15px"
                        />
                      </Box>
                    )}
                  </MenuItem>
                );
              })}
            </Menu.Content>
          </Menu.Positioner>
        </Portal>
      </Menu.Root>
    </div>
  );
};

const toggleValue = (array, value) => {
  return array.includes(value) ? array.filter((v) => v !== value) : [...array, value];
};

export default MixtapeFilterSort;
